import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'

import {
  buildIssueCommentsAuthorList,
  buildThreadPreviewsAuthorList,
  sortThreadPreviewsByDiffOrder,
} from '../../helpers/comments-helper'
import type {IssueComment} from '../../page-data/payloads/issue-comments'
import {useDiffPathOrderMap, useIssueComments, useReviewSummaries, useThreads} from '../../stores/selectors'
import type {AuthorFilterItem} from './CommentsFilter'
import {conversationItemToIssueComment} from './utils/conversation-item-to-issue-comment'
import {buildConversationItems, type ConversationItem} from './utils/build-conversation-items'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface CommentsData {
  /** Thread previews sorted by their position in the diff. */
  threadPreviews: Thread[]
  /** The unfiltered number of diff comments, used for "showing X of Y" messaging. */
  totalDiffCommentCount: number
  /** Issue comments and review summaries sorted chronologically, preserving their original types. */
  conversationItems: ConversationItem[]
  /** Issue comments and review summaries flattened to a single shape, for filtering. */
  mergedConversationComments: IssueComment[]
  /** Unique authors of the thread previews, for the author filter. */
  threadPreviewsAuthorList: AuthorFilterItem[]
  /** Unique authors of the conversation comments, for the author filter. */
  issueCommentsAuthorList: AuthorFilterItem[]
}

const emptyArray = [] as const

/**
 * Derives the comment collections rendered by `Comments` and its view controls from the
 * pull request stores.
 */
export function useCommentsData(): CommentsData {
  const unsortedThreadPreviews = useThreads()
  const diffPathOrderMap = useDiffPathOrderMap()
  const issueComments = useIssueComments()
  const reviewSummaries = useReviewSummaries()

  const threadPreviews = useMemo(
    () => sortThreadPreviewsByDiffOrder(unsortedThreadPreviews, diffPathOrderMap),
    [unsortedThreadPreviews, diffPathOrderMap],
  )

  // Separate memo for the flagged array avoids extra `buildConversationItems` calls if threads change when flag is disabled
  const threadPreviewsForItems = useMemo(
    () => (isFeatureEnabled('prs_docked_comments_replies') ? threadPreviews : emptyArray),
    [threadPreviews],
  )

  // Build a chronologically sorted list of conversation items (issue comments + review summaries)
  // preserving original types so each renders with its dedicated component
  const conversationItems = useMemo(
    () => buildConversationItems(issueComments, reviewSummaries, threadPreviewsForItems),
    [issueComments, reviewSummaries, threadPreviewsForItems],
  )

  // Flatten to IssueComment shape for filtering (useFilterIssueCommentsIds expects IssueComment[])
  const mergedConversationComments = useMemo(
    () => conversationItems.map(item => conversationItemToIssueComment(item)).filter(item => item !== null),
    [conversationItems],
  )

  const threadPreviewsAuthorList = useMemo(() => buildThreadPreviewsAuthorList(threadPreviews), [threadPreviews])

  const issueCommentsAuthorList = useMemo(
    () => buildIssueCommentsAuthorList(mergedConversationComments),
    [mergedConversationComments],
  )

  return {
    threadPreviews,
    totalDiffCommentCount: unsortedThreadPreviews.length,
    conversationItems,
    mergedConversationComments,
    threadPreviewsAuthorList,
    issueCommentsAuthorList,
  }
}
